import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMessageConversation,
  fetchMessagingInbox,
  fetchTeamMessageConversation,
  markMessageConversationUnread,
  markTeamMessageConversationUnread,
  sendDirectAppMessage,
  sendTeamAppMessage,
} from '../api/auth';
import { fetchSavedReports } from '../api/ops';
import styles from './DashboardMessagingCenter.module.css';

const EMPTY_INBOX = {
  contacts: [],
  threads: [],
  unreadTotal: 0,
  defaultContactId: null,
};

function formatTime(value, language) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(language || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildAvatarLabel(contact = {}) {
  const base = contact.fullName || contact.username || 'Smarti user';
  return base
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'SU';
}

function getContactRoleLabel(contact = {}) {
  if (contact.isSuperadmin) return 'Superadmin';
  if (contact.role === 'admin') return 'Admin';
  return 'User';
}

function getContactDescriptor(contact = {}) {
  const parts = [getContactRoleLabel(contact)];
  if (contact.company) parts.push(contact.company);
  if (contact.team) parts.push(contact.team);
  return parts.join(' · ');
}

function formatPresenceLastSeen(value, locale) {
  if (!value) return 'Offline';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Offline';
  const now = Date.now();
  const diffMinutes = Math.max(0, Math.round((now - date.getTime()) / 60000));
  if (diffMinutes < 1) return 'Last seen just now';
  if (diffMinutes < 60) return `Last seen ${diffMinutes} min ago`;
  if (diffMinutes < 24 * 60) return `Last seen ${Math.round(diffMinutes / 60)} hr ago`;
  return `Last seen ${date.toLocaleDateString(locale || undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}

function getPresenceLabel(contact = {}, locale) {
  if (contact?.presence?.isOnline) return 'Online';
  return formatPresenceLastSeen(contact?.presence?.lastSeenAt, locale);
}

function getMessageStatus(message) {
  if (!message?.isOwnMessage) return '';
  return message.readAt ? 'seen' : 'delivered';
}

function getMessageStatusLabel(status, t) {
  if (status === 'seen') return t('Seen');
  if (status === 'delivered') return t('Delivered');
  return '';
}

function getThreadTitle(thread = {}) {
  const contact = thread.contact || thread.group?.admin || {};
  if (thread.threadType === 'group') {
    const title = thread.group?.title || `${contact.fullName || contact.username || 'Team'} team`;
    return `${title} · ${thread.group?.members?.length || 0} members`;
  }
  return contact.fullName || contact.username || 'Smarti user';
}

function threadHasReport(thread = {}) {
  return Boolean(thread.latestMessage?.reportAttachment || thread.latestMessage?.reportId || /\breport\b/i.test(thread.latestPreview || ''));
}

function getSavedReportDisplayName(report) {
  const title = String(report?.title || '').trim();
  const fileName = String(report?.fileName || '').trim();
  if (title && title.toLowerCase() !== 'smarti report') return title;
  if (fileName) return fileName.replace(/\.(pdf|docx)$/i, '');
  return title || 'Smarti report';
}

function getAttachmentIcon(message = {}) {
  if (message.reportAttachment) {
    const name = String(message.reportAttachment.fileName || '').toLowerCase();
    return name.endsWith('.docx') ? 'DOCX' : 'PDF';
  }
  if (message.imageDataUrl) return 'IMG';
  if (message.fileAttachment) {
    const name = String(message.fileAttachment.fileOriginalName || message.fileAttachment.fileName || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'PDF';
    if (name.endsWith('.docx') || name.endsWith('.doc')) return 'DOCX';
    if (message.fileAttachment.fileMimeType?.startsWith?.('image/')) return 'IMG';
    return 'FILE';
  }
  return '';
}

function dateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatDateSeparator(value, language) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(language || undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function readPinnedThreads() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem('smarti:pinnedMessageThreads') || '[]');
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function savePinnedThreads(threadKeys) {
  try {
    window.localStorage.setItem('smarti:pinnedMessageThreads', JSON.stringify(threadKeys));
  } catch {
    // Local pinning is a convenience feature; storage failures should not block messaging.
  }
}

const MESSAGE_TEMPLATES = [
  { key: 'issue', label: 'Issue', text: 'Issue: \nImpact: \nWhat I tried: ' },
  { key: 'approval', label: 'Approval', text: 'Approval needed: \nReason: \nDeadline: ' },
  { key: 'report_sent', label: 'Report sent', text: 'Report sent. Please review and let me know if you want changes.' },
];

const IMAGE_ATTACHMENT_LIMIT_BYTES = 2_000_000;
const DOCUMENT_ATTACHMENT_LIMIT_BYTES = 4_000_000;
const MAX_IMAGE_DIMENSION = 1920;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob) {
  return readFileAsDataUrl(blob);
}

function dataUrlToBlob(dataUrl) {
  const [header, encoded = ''] = String(dataUrl || '').split(',');
  const mime = header.match(/^data:([^;]+);base64$/i)?.[1] || 'application/octet-stream';
  const binary = window.atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the selected image.'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not optimize the selected image.'));
    }, mimeType, quality);
  });
}

function withJpegExtension(fileName = 'image') {
  const base = String(fileName || 'image').replace(/\.[^.]+$/, '') || 'image';
  return `${base}.jpg`;
}

async function optimizeImageForUpload(file, limitBytes = IMAGE_ATTACHMENT_LIMIT_BYTES) {
  if (file.size <= limitBytes) {
    return {
      fileName: file.name || 'screenshot',
      dataUrl: await readFileAsDataUrl(file),
      optimized: false,
    };
  }

  const image = await loadImageElement(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
  canvas.height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not optimize the selected image.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const mimeType = 'image/jpeg';
  let quality = 0.88;
  let blob = await canvasToBlob(canvas, mimeType, quality);
  while (blob.size > limitBytes && quality > 0.48) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, mimeType, quality);
  }
  while (blob.size > limitBytes && canvas.width > 640 && canvas.height > 640) {
    const nextWidth = Math.max(1, Math.round(canvas.width * 0.86));
    const nextHeight = Math.max(1, Math.round(canvas.height * 0.86));
    const nextCanvas = document.createElement('canvas');
    nextCanvas.width = nextWidth;
    nextCanvas.height = nextHeight;
    const nextContext = nextCanvas.getContext('2d');
    if (!nextContext) break;
    nextContext.drawImage(canvas, 0, 0, nextWidth, nextHeight);
    canvas.width = nextWidth;
    canvas.height = nextHeight;
    context.drawImage(nextCanvas, 0, 0);
    blob = await canvasToBlob(canvas, mimeType, Math.max(quality, 0.5));
  }
  if (blob.size > limitBytes) {
    throw new Error('This image is too large to optimize for messaging. Try cropping it first.');
  }
  return {
    fileName: withJpegExtension(file.name),
    dataUrl: await blobToDataUrl(blob),
    optimized: true,
  };
}

async function compressDocumentForUpload(file, limitBytes = DOCUMENT_ATTACHMENT_LIMIT_BYTES) {
  if (file.size <= limitBytes) {
    return {
      fileName: file.name,
      fileMimeType: file.type || 'application/octet-stream',
      fileDataUrl: await readFileAsDataUrl(file),
      fileIsCompressed: false,
      fileOriginalName: '',
      fileOriginalMimeType: '',
    };
  }
  if (typeof CompressionStream === 'undefined') {
    throw new Error('This browser cannot auto-compress large documents. Choose a file under 4 MB.');
  }
  const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
  const compressedBlob = await new Response(stream).blob();
  if (compressedBlob.size > limitBytes) {
    throw new Error('This document is still too large after compression. Choose a smaller file.');
  }
  return {
    fileName: `${file.name}.gz`,
    fileMimeType: 'application/gzip',
    fileDataUrl: await blobToDataUrl(compressedBlob),
    fileIsCompressed: true,
    fileOriginalName: file.name,
    fileOriginalMimeType: file.type || 'application/octet-stream',
  };
}

async function downloadFileAttachment(fileAttachment) {
  if (!fileAttachment?.fileDataUrl) return;
  let blob = dataUrlToBlob(fileAttachment.fileDataUrl);
  let fileName = fileAttachment.fileName || 'attachment';
  if (fileAttachment.fileIsCompressed) {
    fileName = fileAttachment.fileOriginalName || fileName.replace(/\.gz$/i, '') || 'attachment';
    if (typeof DecompressionStream !== 'undefined') {
      const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
      blob = await new Response(stream).blob();
      blob = new Blob([blob], { type: fileAttachment.fileOriginalMimeType || 'application/octet-stream' });
    }
  }
  downloadBlob(blob, fileName);
}

export default function DashboardMessagingCenter({ currentUser, cardClassName = '', badgeClassName = '' }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inbox, setInbox] = useState(EMPTY_INBOX);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedThreadKey, setSelectedThreadKey] = useState('');
  const [conversation, setConversation] = useState({ contact: null, group: null, messages: [] });
  const [messageText, setMessageText] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [fileAttachment, setFileAttachment] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [messageKind, setMessageKind] = useState('message');
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [mentionUserIds, setMentionUserIds] = useState([]);
  const [conversationSearch, setConversationSearch] = useState('');
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [showReportThreads, setShowReportThreads] = useState(false);
  const [pinnedThreadKeys, setPinnedThreadKeys] = useState(() => readPinnedThreads());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pendingDeepLinkContactId, setPendingDeepLinkContactId] = useState(null);
  const endRef = useRef(null);

  const refreshInbox = useCallback(async () => {
    setLoadingInbox(true);
    try {
      const result = await fetchMessagingInbox();
      setInbox(result || EMPTY_INBOX);
      setError('');
      return result || EMPTY_INBOX;
    } catch (loadError) {
      setError(loadError.message || 'Could not load your messaging inbox.');
      return EMPTY_INBOX;
    } finally {
      setLoadingInbox(false);
    }
  }, []);

  const loadConversation = useCallback(async (contactId) => {
    if (!contactId) return;
    setLoadingConversation(true);
    try {
      const result = await fetchMessageConversation(contactId);
      setConversation(result || { contact: null, group: null, messages: [] });
      setSelectedContactId(Number(contactId));
      setSelectedThreadKey(`direct:${contactId}`);
      setError('');
      const refreshed = await fetchMessagingInbox();
      setInbox(refreshed || EMPTY_INBOX);
    } catch (loadError) {
      setError(loadError.message || 'Could not load this conversation.');
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  const loadTeamConversation = useCallback(async (teamAdminUserId) => {
    if (!teamAdminUserId) return;
    setLoadingConversation(true);
    try {
      const result = await fetchTeamMessageConversation(teamAdminUserId);
      setConversation(result || { contact: null, group: null, messages: [] });
      setSelectedContactId(null);
      setSelectedThreadKey(`group:${teamAdminUserId}`);
      setError('');
      const refreshed = await fetchMessagingInbox();
      setInbox(refreshed || EMPTY_INBOX);
    } catch (loadError) {
      setError(loadError.message || 'Could not load this team conversation.');
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  useEffect(() => {
    refreshInbox();
  }, [refreshInbox]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      refreshInbox();
      if (open && selectedThreadKey?.startsWith('group:')) {
        loadTeamConversation(Number(selectedThreadKey.split(':')[1]));
      } else if (open && selectedContactId) {
        loadConversation(selectedContactId);
      }
    }, 20000);
    return () => window.clearInterval(interval);
  }, [loadConversation, loadTeamConversation, open, refreshInbox, selectedContactId, selectedThreadKey]);

  useEffect(() => {
    if (!open) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversation.messages, open]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openMessages') !== '1') return;

    const contactId = Number(searchParams.get('contact') || 0);
    if (Number.isInteger(contactId) && contactId > 0) {
      setPendingDeepLinkContactId(contactId);
    } else {
      setPendingDeepLinkContactId(null);
    }

    setOpen(true);
    navigate('/dashboard', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    if (!open) return;
    const preferredThreadKey = pendingDeepLinkContactId
      ? `direct:${pendingDeepLinkContactId}`
      : selectedThreadKey || inbox.defaultThreadKey || (inbox.defaultContactId ? `direct:${inbox.defaultContactId}` : null) || inbox.threads[0]?.threadKey || null;
    if (preferredThreadKey && preferredThreadKey !== selectedThreadKey) {
      if (preferredThreadKey.startsWith('group:')) {
        loadTeamConversation(Number(preferredThreadKey.split(':')[1]));
      } else {
        const preferredContactId = Number(preferredThreadKey.split(':')[1]);
      loadConversation(preferredContactId);
      }
    }
    if (pendingDeepLinkContactId) {
      setPendingDeepLinkContactId(null);
    }
  }, [inbox.defaultContactId, inbox.defaultThreadKey, inbox.threads, loadConversation, loadTeamConversation, open, pendingDeepLinkContactId, selectedThreadKey]);

  const threadList = useMemo(() => inbox.threads || [], [inbox.threads]);
  const filteredThreadList = useMemo(() => {
    const query = String(conversationSearch || '').trim().toLowerCase();
    return threadList
      .filter((thread) => !showOnlyUnread || Number(thread.unreadCount || 0) > 0)
      .filter((thread) => !showReportThreads || threadHasReport(thread))
      .filter((thread) => {
        if (!query) return true;
      const contact = thread.contact || thread.group?.admin || {};
      const haystack = [
        thread.group?.title,
        contact.fullName,
        contact.username,
        contact.email,
        contact.company,
        contact.team,
        thread.latestPreview,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
      })
      .sort((left, right) => {
        const leftPinned = pinnedThreadKeys.includes(left.threadKey) ? 1 : 0;
        const rightPinned = pinnedThreadKeys.includes(right.threadKey) ? 1 : 0;
        return rightPinned - leftPinned;
      });
  }, [conversationSearch, pinnedThreadKeys, showOnlyUnread, showReportThreads, threadList]);
  const selectedThread = useMemo(
    () => threadList.find((thread) => thread.threadKey === selectedThreadKey) || null,
    [selectedThreadKey, threadList]
  );
  const selectedContact = selectedThread?.contact || conversation.contact || null;
  const selectedGroup = selectedThread?.group || conversation.group || null;
  const activeTitle = selectedGroup?.title || selectedContact?.fullName || selectedContact?.username || '';
  const activeMembers = selectedGroup?.members || [];
  const selectedReport = useMemo(
    () => savedReports.find((report) => Number(report.id) === Number(selectedReportId)) || null,
    [savedReports, selectedReportId]
  );
  const canSend = Boolean(String(messageText || '').trim() || imageDataUrl || selectedReportId || fileAttachment);

  const togglePinnedThread = (threadKey) => {
    setPinnedThreadKeys((current) => {
      const next = current.includes(threadKey)
        ? current.filter((key) => key !== threadKey)
        : [threadKey, ...current];
      savePinnedThreads(next);
      return next;
    });
  };

  const handleMarkUnread = async () => {
    if (!selectedThreadKey) return;
    try {
      const result = selectedThreadKey.startsWith('group:')
        ? await markTeamMessageConversationUnread(Number(selectedThreadKey.split(':')[1]))
        : await markMessageConversationUnread(selectedContactId);
      if (result?.inbox) setInbox(result.inbox);
      setNotice(result?.message || 'Conversation marked as unread.');
    } catch (markError) {
      setError(markError.message || 'Could not mark the conversation unread.');
    }
  };

  const copyMessage = async (message) => {
    const parts = [
      message.bodyText,
      message.reportAttachment?.fileName ? `Report: ${message.reportAttachment.fileName}` : '',
      message.fileAttachment?.fileName ? `Attachment: ${message.fileAttachment.fileOriginalName || message.fileAttachment.fileName}` : '',
      message.imageDataUrl ? 'Image attachment' : '',
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(parts.join('\n') || 'Message attachment');
      setNotice('Message copied.');
    } catch {
      setError('Could not copy the message.');
    }
  };

  const applyTemplate = (template) => {
    setMessageText((current) => {
      const prefix = String(current || '').trim();
      return prefix ? `${prefix}\n\n${template.text}` : template.text;
    });
    if (template.key === 'issue') {
      setPriority('urgent');
    }
    if (template.key === 'approval') {
      setMessageKind('report_request');
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setNotice('');
    const [currentInbox] = await Promise.all([
      refreshInbox(),
      fetchSavedReports()
        .then((result) => setSavedReports(Array.isArray(result.reports) ? result.reports : []))
        .catch(() => setSavedReports([])),
    ]);
    const nextThreadKey = selectedThreadKey || currentInbox.defaultThreadKey || currentInbox.threads?.[0]?.threadKey || (currentInbox.defaultContactId ? `direct:${currentInbox.defaultContactId}` : null);
    if (nextThreadKey?.startsWith('group:')) {
      await loadTeamConversation(Number(nextThreadKey.split(':')[1]));
    } else if (nextThreadKey) {
      const nextContactId = Number(nextThreadKey.split(':')[1]);
      await loadConversation(nextContactId);
    }
  };

  const handleImagePicked = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
        throw new Error('Please choose a PNG, JPEG, or WebP image.');
      }
      const optimized = await optimizeImageForUpload(file);
      setImageDataUrl(optimized.dataUrl);
      setError('');
      setNotice(optimized.optimized ? 'Image was optimized to fit the message size limit.' : '');
    } catch (uploadError) {
      setError(uploadError.message || 'Could not read the selected image.');
    } finally {
      event.target.value = '';
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      let nextAttachment;
      if (/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
        const optimized = await optimizeImageForUpload(file, DOCUMENT_ATTACHMENT_LIMIT_BYTES);
        nextAttachment = {
          fileName: optimized.fileName,
          fileMimeType: 'image/jpeg',
          fileDataUrl: optimized.dataUrl,
          fileIsCompressed: false,
          fileOriginalName: '',
          fileOriginalMimeType: '',
        };
        setNotice(optimized.optimized ? 'Image document was optimized to fit the message size limit.' : '');
      } else {
        nextAttachment = await compressDocumentForUpload(file);
        setNotice(nextAttachment.fileIsCompressed ? 'Document was compressed for messaging and will download as the original file.' : '');
      }
      setFileAttachment(nextAttachment);
      setError('');
    } catch (uploadError) {
      setError(uploadError.message || 'Could not read the selected document.');
    } finally {
      event.target.value = '';
    }
  };

  const handleSend = async () => {
    if (!selectedThreadKey) {
      setError('Choose a conversation first.');
      return;
    }
    setSending(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        text: messageText,
        imageDataUrl,
        reportId: selectedReportId ? Number(selectedReportId) : null,
        fileName: fileAttachment?.fileName || '',
        fileMimeType: fileAttachment?.fileMimeType || '',
        fileDataUrl: fileAttachment?.fileDataUrl || '',
        fileIsCompressed: Boolean(fileAttachment?.fileIsCompressed),
        fileOriginalName: fileAttachment?.fileOriginalName || '',
        fileOriginalMimeType: fileAttachment?.fileOriginalMimeType || '',
        priority,
        messageKind,
        parentMessageId: replyToMessage?.id || null,
        mentionUserIds,
      };
      const result = selectedThreadKey.startsWith('group:')
        ? await sendTeamAppMessage({ ...payload, teamAdminUserId: Number(selectedThreadKey.split(':')[1]) })
        : await sendDirectAppMessage({ ...payload, recipientUserId: selectedContactId });
      setMessageText('');
      setImageDataUrl('');
      setFileAttachment(null);
      setSelectedReportId('');
      setPriority('normal');
      setMessageKind('message');
      setReplyToMessage(null);
      setMentionUserIds([]);
      setNotice(typeof result.message === 'string' ? result.message : 'Message sent successfully.');
      if (result.inbox) setInbox(result.inbox);
      if (selectedThreadKey.startsWith('group:')) {
        await loadTeamConversation(Number(selectedThreadKey.split(':')[1]));
      } else {
        await loadConversation(selectedContactId);
      }
    } catch (sendError) {
      setError(sendError.message || 'Could not send the message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`${cardClassName} ${styles.messagingCard} ${Number(inbox.unreadTotal || 0) > 0 ? styles.messagingCardAlert : ''}`}
        onClick={handleOpen}
      >
        <div className={styles.cardTop}>
          <span className={badgeClassName}>{t('Messaging')}</span>
          {Number(inbox.unreadTotal || 0) > 0 ? (
            <span className={styles.unreadBadge}>{inbox.unreadTotal}</span>
          ) : null}
        </div>
        <strong>{t('Inbox and support chat')}</strong>
        <p>
          {t('Message your group admin, superadmin, or managed users directly inside Smarti. Send text updates or screenshots when something goes wrong.')}
        </p>
        <em>
          {inbox.unreadTotal
            ? t('{{count}} unread message(s) waiting for you.', { count: inbox.unreadTotal })
            : t('Direct support, troubleshooting, and internal coordination stay in one place.')}
        </em>
      </button>

      {open ? (
        <div className={styles.overlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <div className={styles.header}>
              <div>
                <p className={styles.eyebrow}>{t('Smarti messaging')}</p>
                <h2>{t('Team inbox')}</h2>
                <p>{t('Exchange messages, screenshots, and troubleshooting notes with your Smarti contacts without leaving the app.')}</p>
              </div>
              <button type="button" className={styles.closeButton} onClick={() => setOpen(false)}>
                {t('Close')}
              </button>
            </div>

            {error ? <div className={`${styles.notice} ${styles.errorNotice}`}>{error}</div> : null}
            {notice ? <div className={`${styles.notice} ${styles.successNotice}`}>{notice}</div> : null}

            <div className={`${styles.layout} ${detailsOpen ? styles.layoutDetailsOpen : ''}`}>
              <aside className={styles.sidebar}>
                <div className={styles.sidebarTop}>
                  <div>
                    <strong>{t('Users')}</strong>
                    <p className={styles.sidebarLead}>
                      {Number(inbox.unreadTotal || 0) > 0
                        ? t('{{count}} unread message(s)', { count: inbox.unreadTotal })
                        : t('{{count}} contact(s)', { count: threadList.length })}
                    </p>
                  </div>
                  <button type="button" className={styles.refreshButton} onClick={refreshInbox} disabled={loadingInbox}>
                    {loadingInbox ? t('Refreshing...') : t('Refresh')}
                  </button>
                </div>

                <label className={styles.searchField}>
                  <span>{t('Search inbox')}</span>
                  <input
                    value={conversationSearch}
                    onChange={(event) => setConversationSearch(event.target.value)}
                    placeholder={t('Search by name, email, company, or preview...')}
                  />
                </label>
                <div className={styles.threadFilters}>
                  <button
                    type="button"
                    className={`${styles.filterChip} ${showOnlyUnread ? styles.filterChipActive : ''}`}
                    onClick={() => setShowOnlyUnread((current) => !current)}
                  >
                    {t('Only unread')}
                  </button>
                  <button
                    type="button"
                    className={`${styles.filterChip} ${showReportThreads ? styles.filterChipActive : ''}`}
                    onClick={() => setShowReportThreads((current) => !current)}
                  >
                    {t('Reports')}
                  </button>
                </div>

                <div className={styles.threadList}>
                  {filteredThreadList.length ? filteredThreadList.map((thread) => {
                    const isGroup = thread.threadType === 'group';
                    const contact = thread.contact || thread.group?.admin || {};
                    const isActive = thread.threadKey === selectedThreadKey;
                    return (
                      <div
                        key={thread.threadKey || contact.id}
                        className={`${styles.threadRow} ${isActive ? styles.threadButtonActive : ''}`}
                      >
                        <button
                          type="button"
                          className={styles.threadButton}
                          onClick={() => {
                            if (isGroup) loadTeamConversation(thread.group.teamAdminUserId);
                            else loadConversation(contact.id);
                          }}
                        >
                          {contact.avatarDataUrl ? (
                            <img src={contact.avatarDataUrl} alt={contact.fullName || contact.username || 'User'} className={styles.threadAvatar} />
                          ) : (
                            <div className={styles.threadAvatarFallback}>{buildAvatarLabel(contact)}</div>
                          )}
                          <div className={styles.threadMeta}>
                            <div className={styles.threadHeadingRow}>
                              <strong>{getThreadTitle(thread)}</strong>
                              {thread.unreadCount ? <span className={styles.threadUnread}>{thread.unreadCount}</span> : null}
                            </div>
                            <span>{isGroup ? t('Team group') : getContactDescriptor(contact)}</span>
                            {!isGroup ? (
                              <span className={`${styles.presenceLabel} ${contact?.presence?.isOnline ? styles.presenceOnline : styles.presenceOffline}`}>
                                {t(getPresenceLabel(contact, i18n.language))}
                              </span>
                            ) : null}
                            <p>{thread.latestPreview}</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          className={`${styles.pinButton} ${pinnedThreadKeys.includes(thread.threadKey) ? styles.pinButtonActive : ''}`}
                          onClick={() => togglePinnedThread(thread.threadKey)}
                          title={pinnedThreadKeys.includes(thread.threadKey) ? t('Unpin conversation') : t('Pin conversation')}
                        >
                          {pinnedThreadKeys.includes(thread.threadKey) ? '★' : '☆'}
                        </button>
                      </div>
                    );
                  }) : (
                    <div className={styles.emptyState}>
                      {conversationSearch
                        ? t('No conversations matched your search.')
                        : t('No messaging contacts are available yet.')}
                    </div>
                  )}
                </div>
              </aside>

              <section className={styles.chatPanel}>
                {selectedContact || selectedGroup ? (
                  <>
                    <div className={styles.chatHeader}>
                      <div className={styles.chatHeaderIdentity}>
                        {selectedGroup ? (
                          <div className={styles.chatHeaderAvatarFallback}>TM</div>
                        ) : selectedContact.avatarDataUrl ? (
                          <img src={selectedContact.avatarDataUrl} alt={selectedContact.fullName || selectedContact.username || 'User'} className={styles.chatHeaderAvatar} />
                        ) : (
                          <div className={styles.chatHeaderAvatarFallback}>{buildAvatarLabel(selectedContact)}</div>
                        )}
                        <div className={styles.chatHeaderIdentityText}>
                          <strong>{activeTitle}</strong>
                          <span>{selectedGroup ? t('{{count}} members', { count: activeMembers.length }) : getContactDescriptor(selectedContact)}</span>
                          {!selectedGroup ? (
                            <span className={`${styles.presenceLabel} ${selectedContact?.presence?.isOnline ? styles.presenceOnline : styles.presenceOffline}`}>
                              {t(getPresenceLabel(selectedContact, i18n.language))}
                            </span>
                          ) : null}
                          <div className={styles.chatHeaderTags}>
                            <span className={styles.headerTag}>{selectedGroup ? t('Team group') : getContactRoleLabel(selectedContact)}</span>
                            {selectedGroup ? <span className={styles.headerTag}>{t('Auto-updated members')}</span> : null}
                            {selectedContact?.department ? <span className={styles.headerTag}>{selectedContact.department}</span> : null}
                            {selectedContact?.profileTitle ? <span className={styles.headerTag}>{selectedContact.profileTitle}</span> : null}
                          </div>
                        </div>
                      </div>
                      <div className={styles.chatHeaderMeta}>
                        <span>{selectedGroup ? selectedGroup.admin?.username : (selectedContact.email || currentUser?.email || '')}</span>
                        <p>{selectedGroup ? t('Announcements, reports, mentions, issue replies, and team audit messages stay here.') : t('Share errors, screenshots, and updates directly in Smarti.')}</p>
                        <div className={styles.chatHeaderActions}>
                          <button type="button" className={styles.headerActionButton} onClick={() => togglePinnedThread(selectedThreadKey)}>
                            {pinnedThreadKeys.includes(selectedThreadKey) ? t('Unpin') : t('Pin')}
                          </button>
                          <button type="button" className={styles.headerActionButton} onClick={handleMarkUnread}>
                            {t('Mark unread')}
                          </button>
                          <button type="button" className={styles.headerActionButton} onClick={() => setDetailsOpen((current) => !current)}>
                            {detailsOpen ? t('Hide details') : t('Details')}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className={styles.messages}>
                      {loadingConversation ? (
                        <div className={styles.emptyState}>{t('Loading conversation...')}</div>
                      ) : conversation.messages?.length ? conversation.messages.map((message, index) => {
                        const status = getMessageStatus(message);
                        const previousMessage = conversation.messages[index - 1];
                        const showDate = dateKey(previousMessage?.createdAt) !== dateKey(message.createdAt);
                        const attachmentIcon = getAttachmentIcon(message);
                        return (
                          <React.Fragment key={message.id}>
                          {showDate ? (
                            <div className={styles.dateSeparator}>{formatDateSeparator(message.createdAt, i18n.language)}</div>
                          ) : null}
                          <article
                            className={`${styles.messageBubble} ${message.isOwnMessage ? styles.ownBubble : styles.incomingBubble}`}
                          >
                            {attachmentIcon ? <span className={styles.attachmentTypeBadge}>{attachmentIcon}</span> : null}
                            {message.bodyText ? <p>{message.bodyText}</p> : null}
                            {message.parentMessageId ? <span className={styles.replyMarker}>{t('Reply to message #{{id}}', { id: message.parentMessageId })}</span> : null}
                            {message.priority && message.priority !== 'normal' ? <span className={`${styles.priorityBadge} ${message.priority === 'blocked' ? styles.blockedBadge : styles.urgentBadge}`}>{t(message.priority)}</span> : null}
                            {message.messageKind && message.messageKind !== 'message' ? <span className={styles.kindBadge}>{t(message.messageKind)}</span> : null}
                            {message.imageDataUrl ? (
                              <a href={message.imageDataUrl} target="_blank" rel="noreferrer" className={styles.imageLink}>
                                <img src={message.imageDataUrl} alt="Attachment" className={styles.attachmentImage} />
                              </a>
                            ) : null}
                            {message.reportAttachment ? (
                              <a
                                href={message.reportAttachment.dataUrl}
                                download={message.reportAttachment.fileName || 'smarti-report.pdf'}
                                className={styles.reportAttachment}
                              >
                                <span>{t('Report')}</span>
                                <strong>{message.reportAttachment.title || message.reportAttachment.fileName}</strong>
                                <em>{message.reportAttachment.fileName}</em>
                              </a>
                            ) : null}
                            {message.fileAttachment ? (
                              <button
                                type="button"
                                className={`${styles.reportAttachment} ${styles.attachmentDownloadButton}`}
                                onClick={() => {
                                  downloadFileAttachment(message.fileAttachment).catch((downloadError) => {
                                    setError(downloadError.message || 'Could not download the attachment.');
                                  });
                                }}
                              >
                                <span>{t('Document')}</span>
                                <strong>{message.fileAttachment.fileOriginalName || message.fileAttachment.fileName}</strong>
                                <em>{message.fileAttachment.fileIsCompressed ? t('Optimized for upload') : message.fileAttachment.fileMimeType}</em>
                              </button>
                            ) : null}
                            <div className={styles.messageMetaRow}>
                              <span>{formatTime(message.createdAt, i18n.language)}</span>
                              {message.readReceipts?.length ? <span>{t('{{count}} read', { count: message.readReceipts.length })}</span> : null}
                              {status ? (
                                <span
                                  className={`${styles.statusTick} ${status === 'seen' ? styles.statusTickSeen : styles.statusTickDelivered}`}
                                  title={getMessageStatusLabel(status, t)}
                                  aria-label={getMessageStatusLabel(status, t)}
                                >
                                  {status === 'seen' ? '✓✓' : '✓'}
                                </span>
                              ) : null}
                            </div>
                            <div className={styles.messageActionRow}>
                              <button type="button" onClick={() => copyMessage(message)}>{t('Copy')}</button>
                              <button type="button" onClick={() => setReplyToMessage(message)}>{t('Reply')}</button>
                            </div>
                          </article>
                          </React.Fragment>
                        );
                      }) : (
                        <div className={styles.emptyState}>
                          {t('No messages yet. Start the conversation from the composer below.')}
                        </div>
                      )}
                      <div ref={endRef} />
                    </div>

                    <div className={styles.composer}>
                      {replyToMessage ? (
                        <div className={styles.replyPreview}>
                          <span>{t('Replying to message #{{id}}', { id: replyToMessage.id })}</span>
                          <button type="button" className={styles.removeImageButton} onClick={() => setReplyToMessage(null)}>{t('Cancel')}</button>
                        </div>
                      ) : null}
                      {mentionUserIds.length ? (
                        <div className={styles.mentionStrip}>
                          {mentionUserIds.map((id) => {
                            const member = activeMembers.find((entry) => Number(entry.id) === Number(id));
                            return <span key={id}>@{member?.fullName || member?.username || id}</span>;
                          })}
                          <button type="button" className={styles.removeImageButton} onClick={() => setMentionUserIds([])}>{t('Clear mentions')}</button>
                        </div>
                      ) : null}
                      <textarea
                        rows={4}
                        value={messageText}
                        onChange={(event) => setMessageText(event.target.value)}
                        placeholder={t('Write your message here. Explain the issue, ask for help, or add context for your team.')}
                      />
                      <div className={styles.templateRow}>
                        {MESSAGE_TEMPLATES.map((template) => (
                          <button key={template.key} type="button" className={styles.templateButton} onClick={() => applyTemplate(template)}>
                            {t(template.label)}
                          </button>
                        ))}
                      </div>
                      {imageDataUrl ? (
                        <div className={styles.previewStrip}>
                          <img src={imageDataUrl} alt="Preview" className={styles.previewImage} />
                          <button type="button" className={styles.removeImageButton} onClick={() => setImageDataUrl('')}>
                            {t('Remove image')}
                          </button>
                        </div>
                      ) : null}
                      {selectedReport ? (
                        <div className={styles.reportPreviewStrip}>
                          <div>
                            <span>{t('Selected report')}</span>
                            <strong>{getSavedReportDisplayName(selectedReport)}</strong>
                            <p>{selectedReport.fileName}</p>
                          </div>
                          <button type="button" className={styles.removeImageButton} onClick={() => setSelectedReportId('')}>
                            {t('Remove report')}
                          </button>
                        </div>
                      ) : null}
                      {fileAttachment ? (
                        <div className={styles.reportPreviewStrip}>
                          <div>
                            <span>{t('Selected document')}</span>
                            <strong>{fileAttachment.fileOriginalName || fileAttachment.fileName}</strong>
                            <p>{fileAttachment.fileIsCompressed ? t('Optimized for upload') : fileAttachment.fileMimeType}</p>
                          </div>
                          <button type="button" className={styles.removeImageButton} onClick={() => setFileAttachment(null)}>
                            {t('Remove document')}
                          </button>
                        </div>
                      ) : null}
                      <div className={styles.composerActions}>
                        {selectedGroup ? (
                          <label className={styles.reportSelectLabel}>
                            <span>{t('@mention')}</span>
                            <select value="" onChange={(event) => {
                              const value = Number(event.target.value);
                              if (value) setMentionUserIds((current) => Array.from(new Set([...current, value])));
                            }}>
                              <option value="">{t('Add mention')}</option>
                              {activeMembers.map((member) => (
                                <option key={member.id} value={member.id}>{member.fullName || member.username}</option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                        <label className={styles.attachButton}>
                          {t('Attach screenshot')}
                          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleImagePicked} />
                        </label>
                        <label className={styles.attachButton}>
                          {t('Attach document')}
                          <input type="file" accept=".pdf,.csv,.txt,.xls,.xlsx,.doc,.docx,image/png,image/jpeg,image/webp" hidden onChange={handleFilePicked} />
                        </label>
                        <label className={styles.reportSelectLabel}>
                          <span>{t('Document')}</span>
                          <select value={selectedReportId} onChange={(event) => setSelectedReportId(event.target.value)}>
                            <option value="">{t('Attach saved report')}</option>
                            {savedReports.map((report) => (
                              <option key={report.id} value={String(report.id)}>
                                {getSavedReportDisplayName(report)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button type="button" className={styles.sendButton} onClick={handleSend} disabled={sending || !canSend}>
                          {sending ? t('Sending...') : t('Send message')}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    {t('Choose a contact to open your Smarti conversation.')}
                  </div>
                )}
              </section>
              {detailsOpen && (selectedContact || selectedGroup) ? (
                <aside className={styles.detailsDrawer}>
                  <div>
                    <span>{t('Conversation')}</span>
                    <strong>{activeTitle}</strong>
                    <p>{selectedGroup ? t('{{count}} members', { count: activeMembers.length }) : getContactDescriptor(selectedContact)}</p>
                  </div>
                  <div>
                    <span>{t('Status')}</span>
                    <p>{pinnedThreadKeys.includes(selectedThreadKey) ? t('Pinned') : t('Not pinned')}</p>
                    <p>{selectedThread?.unreadCount ? t('{{count}} unread', { count: selectedThread.unreadCount }) : t('No unread messages')}</p>
                  </div>
                  {selectedGroup ? (
                    <div>
                      <span>{t('Members')}</span>
                      <div className={styles.detailsMemberList}>
                        {activeMembers.slice(0, 8).map((member) => (
                          <p key={member.id}>{member.fullName || member.username}</p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span>{t('Contact')}</span>
                      <p>{selectedContact?.email || selectedContact?.username}</p>
                      <p className={`${styles.presenceLabel} ${selectedContact?.presence?.isOnline ? styles.presenceOnline : styles.presenceOffline}`}>
                        {t(getPresenceLabel(selectedContact, i18n.language))}
                      </p>
                      {selectedContact?.company ? <p>{selectedContact.company}</p> : null}
                      {selectedContact?.team ? <p>{selectedContact.team}</p> : null}
                    </div>
                  )}
                </aside>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
